import { Select, Skeleton, Tooltip } from "antd";
import React from "react";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import type { ScheduleSlot } from "../../service/schedulePick.service";
import { EventType, trackEvent } from "../../utils/analytics";
import { resolveClinicZone, scheduleSlotLabel } from "../../utils/clientSchedule";
import { LC_KEYS } from "../../utils/constants";
import { VISIBLE_SLOTS } from "./quickStart";
import { CaretRightIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon, TemplateIcon, ZapIcon } from "./quickStartIcons";
import styles from "./quickstart.module.scss";
import type { QuickStartSchedule, SessionTemplateState } from "./useQuickStart";

/** "Template  Family Medicine SOAP ⌄": the template every Start on the card will use, and its picker. */
export const TemplatePicker = ({ session }: { session: SessionTemplateState }) => {
  const { template } = session;
  // The hint must not sit over the open list of templates.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  if (!session.ready) {
    return <Skeleton.Button active block className={styles.Template__loading} />;
  }
  return (
    <Tooltip
      title={template ? null : "No template on file yet. Start now and choose one when you submit, or pick it here."}
      open={pickerOpen ? false : undefined}
    >
      <Select
        variant="borderless"
        showSearch
        data-testid="quick-start-template"
        className={`${styles.Template} ${template ? "" : styles["Template--pending"]}`}
        popupMatchSelectWidth
        prefix={
          <span className={styles.Template__prefix}>
            <TemplateIcon />
            <span className={styles.Template__label}>Template</span>
          </span>
        }
        suffixIcon={<ChevronDownIcon />}
        value={template?.value}
        placeholder="Select your template"
        options={session.groups}
        optionFilterProp="searchText"
        labelRender={() => template?.name ?? "Select your template"}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (open) session.loadGallery();
        }}
        onChange={(value) => {
          const option = session.options.find((candidate) => candidate.value === value);
          if (option) session.choose(option);
        }}
      />
    </Tooltip>
  );
};

type QuickStartCardProps = {
  schedule: QuickStartSchedule;
  session: SessionTemplateState;
  /** The patient whose recording is starting, while it starts. */
  startingId: number | null;
  onQuickStart: (slot: ScheduleSlot, context: { expanded: boolean }) => void;
  /** × : the schedule's dismiss, wrapped by the caller so it can move focus. */
  onDismiss: () => void;
  dismissRef?: React.Ref<HTMLButtonElement>;
  footer: React.ReactNode;
};

// After the clinician scrolls the list, it stops following the clock for this long.
const FOLLOW_PAUSE_MS = 2 * 60 * 1000;

/**
 * "Who's in the room?": the whole of today's schedule in appointment order, each patient one click
 * from recording with the template shown under the list. The appointment under way (the latest one
 * already due) is outlined with a caret and kept mid-list as the clock moves: a clinician running
 * late picks an earlier patient a scroll up, without ×. Expanded (the default) and collapsed are the
 * same scrolling list at two heights. × closes it and the card becomes Start Encounter.
 */
const QuickStartCard = ({
  schedule,
  session,
  startingId,
  onQuickStart,
  onDismiss,
  dismissRef,
  footer,
}: QuickStartCardProps) => {
  const { sessionTerm, sessionTermLower } = useSpecialityReference();
  const zone = React.useMemo(() => resolveClinicZone(schedule.pick?.clinic_tz), [schedule.pick?.clinic_tz]);
  // Expanded unless the clinician chose Show fewer on this device (kept until logout).
  const [expanded, setExpanded] = React.useState(() => !localStorage.getItem(LC_KEYS.QUICK_START_COLLAPSED));
  // Each Start is described by its row's time and provider: two patients can share a name.
  const rowId = React.useId();
  // The list scrolls; a fade marks that there is more above or below.
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = React.useState({ scrollable: false, atStart: true, atEnd: true });
  const measure = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const next = {
      scrollable: el.scrollHeight > el.clientHeight + 1,
      atStart: el.scrollTop <= 8,
      atEnd: el.scrollTop + el.clientHeight >= el.scrollHeight - 8,
    };
    setScroll((prev) =>
      prev.scrollable === next.scrollable && prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next,
    );
  }, []);

  const { view } = schedule;
  const slots = view.kind === "list" ? view.slots : [];
  // The appointment under way: the latest one already due, or the first of the day before it starts.
  const current = view.kind === "list" ? Math.max(view.now - 1, 0) : 0;
  const toggleable = slots.length > VISIBLE_SLOTS;
  const open = expanded || !toggleable;

  // Keep the current row mid-list: on open, on Show more / fewer, and as the clock moves it. Only a
  // wheel, touch, drag or key on the list counts as the clinician scrolling (never our own scroll),
  // and following resumes FOLLOW_PAUSE_MS after that.
  const userScrolledAt = React.useRef<number | null>(null);
  const centred = React.useRef(false);
  const holdFollow = () => {
    userScrolledAt.current = Date.now();
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-centre when the height or the current row changes
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    const row = el?.querySelector<HTMLElement>("[data-current]");
    if (!el || !row) return;
    const held = userScrolledAt.current !== null && Date.now() - userScrolledAt.current < FOLLOW_PAUSE_MS;
    if (held) return;
    const top = row.offsetTop + row.offsetHeight / 2 - el.clientHeight / 2;
    // The first placement jumps; later ones (the clock moving the row) glide.
    if (centred.current && el.scrollTo) el.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
    else el.scrollTop = Math.max(top, 0);
    centred.current = true;
  }, [current, slots.length]);
  // A height change re-centres at once, whoever scrolled last.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the list changes height
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    const row = el?.querySelector<HTMLElement>("[data-current]");
    if (!el || !row) return;
    userScrolledAt.current = null;
    el.scrollTop = Math.max(row.offsetTop + row.offsetHeight / 2 - el.clientHeight / 2, 0);
  }, [open]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the rows change
  React.useLayoutEffect(measure, [measure, open, slots.length]);

  if (view.kind !== "list") return null;
  // A colleague's patient names its provider; once any row does, every row does, so the list reads evenly.
  const namesProviders = slots.some((slot) => slot.client.is_owned === false);
  const busy = startingId !== null || !session.ready;

  const toggle = () => {
    if (!open) trackEvent(EventType.QUICK_START_LIST_EXPANDED, { candidates: slots.length });
    if (open) localStorage.setItem(LC_KEYS.QUICK_START_COLLAPSED, "1");
    else localStorage.removeItem(LC_KEYS.QUICK_START_COLLAPSED);
    setExpanded(!open);
  };

  return (
    <div className={styles.Card} data-testid="quick-start-card">
      <div className={styles.Room__top}>
        <div className={styles.Room__title} data-testid="quick-start-title">
          Who's in the room?
        </div>
        <div className={styles.Room__chip} data-testid="quick-start-count">
          {slots.length} today
        </div>
        <Tooltip title={`None of these? Close the list and use Start ${sessionTerm}`} trigger={["hover", "focus"]}>
          <button
            type="button"
            ref={dismissRef}
            aria-label="Hide schedule suggestions"
            className={styles.Room__dismiss}
            disabled={startingId !== null}
            onClick={onDismiss}
            data-testid="quick-start-dismiss"
          >
            <CloseIcon size={12} />
          </button>
        </Tooltip>
      </div>

      <div className={styles.Room__list}>
        <div className={styles.Room__scrollWrap}>
          <div
            ref={scrollRef}
            onScroll={measure}
            onWheel={holdFollow}
            onTouchStart={holdFollow}
            onPointerDown={holdFollow}
            onKeyDown={holdFollow}
            className={[
              styles.Room__rows,
              open ? styles["Room__rows--expanded"] : "",
              scroll.scrollable ? styles["Room__rows--scrollable"] : "",
            ].join(" ")}
            data-testid="quick-start-candidates"
          >
            {slots.map((slot, index) => {
              const time = scheduleSlotLabel(slot.scheduled_at, zone);
              const provider = namesProviders ? slot.client.owner_name : null;
              const isCurrent = index === current;
              return (
                <div
                  key={slot.client.id}
                  className={`${styles.Room__row} ${isCurrent ? styles["Room__row--current"] : ""}`}
                  data-current={isCurrent || undefined}
                  aria-current={isCurrent ? "time" : undefined}
                  data-testid="quick-start-candidate"
                >
                  {isCurrent && (
                    <span className={styles.Room__caret}>
                      <CaretRightIcon />
                    </span>
                  )}
                  <div className={styles.Room__text}>
                    <div className={styles.Room__name} title={slot.client.name}>
                      {slot.client.name}
                    </div>
                    <div className={styles.Room__meta} id={`${rowId}-${slot.client.id}`}>
                      {[time, provider].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <button
                    type="button"
                    className={styles.Room__start}
                    aria-label={`Start ${sessionTermLower} with ${slot.client.name}`}
                    aria-describedby={`${rowId}-${slot.client.id}`}
                    disabled={busy}
                    onClick={() => onQuickStart(slot, { expanded: open })}
                    data-testid="quick-start-start"
                  >
                    <ZapIcon size={12} />
                    {startingId === slot.client.id ? "Starting…" : "Start"}
                  </button>
                </div>
              );
            })}
          </div>
          {scroll.scrollable && !scroll.atStart && (
            <div className={`${styles.Room__fade} ${styles["Room__fade--top"]}`} />
          )}
          {scroll.scrollable && !scroll.atEnd && <div className={styles.Room__fade} />}
        </div>
        {/* Show more / fewer on the left, Test Mic and Settings on the right: one line. */}
        <div className={styles.Room__actions}>
          {toggleable && (
            <button
              type="button"
              className={styles.Room__more}
              aria-expanded={open}
              onClick={toggle}
              data-testid="quick-start-more"
            >
              {open ? "Show fewer" : "Show more"}
              {open ? <ChevronUpIcon size={13} /> : <ChevronDownIcon size={13} />}
            </button>
          )}
          {footer}
        </div>
      </div>

      <TemplatePicker session={session} />
    </div>
  );
};

export default QuickStartCard;
